import { observable, action, computed, makeObservable } from 'mobx';
import { UserManager, WebStorageStateStore } from 'oidc-client-ts';
import config from '../config';
import {v4 as uuid} from "uuid";

let instance; // singleton instance
export class AuthStore {
    manager = null;
    user = null;
    roles = [];
    error= null;
    constructor() {
        makeObservable(this, {
            isLoggedIn: computed,
            manager: observable,
            user: observable,
            roles: observable,
            error: observable,
            init: action,
            login: action,
            renew: action,
            completeLogin: action,
            completeRenew: action,
            completeLogout: action,
            handleUserLoaded: action,
            handleUserUnloaded: action,
            logout: action,
            handleError: action,
            hasWriteAccess: action,
        });

        this.login = this.login.bind(this);
        this.completeLogin = this.completeLogin.bind(this);
        this.logout = this.logout.bind(this);
        this.completeLogout = this.completeLogout.bind(this);
        this.handleUserLoaded = this.handleUserLoaded.bind(this);
        this.handleUserUnloaded = this.handleUserUnloaded.bind(this);
        this.hasWriteAccess = this.hasWriteAccess.bind(this);
        this.handleError = this.handleError.bind(this);
    }
    get isLoggedIn() {
        return this.user && !this.user.expired;
    }

    async init() {
        this.manager = new UserManager({
            authority: config.AUTH_ISSUER,
            client_id: config.AUTH_CLIENT_ID,
            redirect_uri: `${config.BASEURL}/auth/handle`,
            silent_redirect_uri: `${config.BASEURL}/auth/renew`,
            automaticSilentRenew: true,
            response_type: 'code',
            scope: 'openid',
            userStore: new WebStorageStateStore({ store: window.localStorage }),
            extraQueryParams: {logout_uri: `${config.BASEURL}/auth/logout`, client_id: config.AUTH_CLIENT_ID}

        });
        this.manager.events.addUserLoaded(this.handleUserLoaded);
        this.manager.events.addUserUnloaded(this.handleUserUnloaded);

        const user = await this.manager.getUser();
        if (user) this.handleUserLoaded(user);
    }

    login() {
        return this.manager.signinRedirect({state: uuid()}).then(response => console.log(response)).catch(error => this.handleError(error));
    }

    renew() {
        return this.manager
            .signinSilent()
            .then(this.manager.storeUser.bind(this.manager))
            .catch(error => this.handleError(error));
    }

    completeLogin() {
        return this.manager.signinRedirectCallback()
            .catch(this.handleError);
    }

    completeRenew() {
        return this.manager.signinSilentCallback().catch(this.handleError);
    }

    hasWriteAccess() {
        return true;
    }

    completeLogout() {
        this.manager.signoutRedirectCallback().catch(this.handleError);
        this.manager.removeUser();
    }

    handleUserLoaded(user) {
        this.user = user;
        if (this.isLoggedIn) {
            this.roles = user.profile['cognito:groups'];

            localStorage.setItem('authorization', `Bearer ${user['id_token']}`);
            this.manager.startSilentRenew();
        } else {
            console.log('logging out')
            this.logout();
        }
        this.manager.clearStaleState();
    }

    handleUserUnloaded() {
        this.user = null;
        this.roles = [];
        this.error = null;
        window.localStorage.clear();
    }

    logout() {
        // FIX - Logout behavior where the user is given an error message claiming a missing client_id after being redirected to <cognito-domain>/login
        // Cognito logout behaviour requires to either:
        // 1- provide a logout_uri and a client_id on the request, specify on Cognito settings an allowed Sign-Out URL that matches the provided logout_uri -> redirect the request to that login_uri
        // 2 - do not specify anything -> redirect the request to <cognito-domain>/login
        // https://docs.aws.amazon.com/cognito/latest/developerguide/logout-endpoint.html
        // This has worked in the past without any query params.
        // This should be just a workaround since the library used for OIDC is, at this time, 3 years old and unmaintained.
        console.log("logout")
        return this.manager.signoutRedirect();
    }

    getRootURIForUser() {
        return !this.isLoggedIn ? '/auth' : '/auth/token';
    }

    handleError(error) {
        if(error) this.error = error
        console.error('Auth Error: ', error);
    }

    static instance() {
        if (!instance) {
            instance = new AuthStore();
        }
        return instance;
    }
}
